
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";

// Demo mode flag - should match the flag in useAuth.tsx
const DEMO_MODE = true;

// Mock demo patients data
const demoPatients: Patient[] = [
  {
    id: 'demo-patient-1',
    first_name: 'Alice',
    last_name: 'Johnson',
    email: 'alice.johnson@email.com',
    phone: '+1-555-0111',
    created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 1 week ago
    lastTriage: {
      urgency: 'moderate',
      created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
      symptoms: ['headache', 'fever', 'fatigue']
    },
    nextAppointment: {
      start_time: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString() // 3 days from now
    }
  },
  {
    id: 'demo-patient-2', 
    first_name: 'Robert',
    last_name: 'Chen',
    email: 'robert.chen@email.com',
    phone: '+1-555-0222',
    created_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(), // 2 weeks ago
    lastTriage: {
      urgency: 'urgent',
      created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
      symptoms: ['chest pain', 'shortness of breath']
    }
  },
  {
    id: 'demo-patient-3',
    first_name: 'Maria',
    last_name: 'Garcia',
    email: 'maria.garcia@email.com',
    phone: '+1-555-0333',
    created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // 1 month ago
    nextAppointment: {
      start_time: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 1 week from now
    }
  }
];

interface Patient {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  created_at: string;
  lastTriage?: {
    urgency: string;
    created_at: string;
    symptoms: string[];
  };
  nextAppointment?: {
    start_time: string;
  };
}

export function usePatientData(clinicId: string, searchQuery: string) {
  const queryResult = useQuery({
    queryKey: ['clinic-patients', clinicId, searchQuery],
    queryFn: async (): Promise<Patient[]> => {
      console.log("🔍 usePatientData - Starting data fetch");
      console.log("🏥 clinicId:", clinicId, typeof clinicId);
      console.log("🔍 searchQuery:", searchQuery);

      // Return mock data in demo mode
      if (DEMO_MODE) {
        console.log("Demo mode - returning mock patient data");
        
        // Filter demo patients based on search query if provided
        if (searchQuery && searchQuery.trim()) {
          const filtered = demoPatients.filter(patient => 
            patient.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            patient.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            patient.email.toLowerCase().includes(searchQuery.toLowerCase())
          );
          console.log(`Demo mode - filtered ${filtered.length} patients for query: "${searchQuery}"`);
          return filtered;
        }
        
        console.log(`Demo mode - returning all ${demoPatients.length} demo patients`);
        return demoPatients;
      }

      // Validation check for empty or invalid clinicId
      if (!clinicId || clinicId === "undefined" || clinicId === "null") {
        console.error("❌ ERROR: Invalid or empty clinicId:", clinicId);
        return [];
      }

      try {
        // Step 1: Query patients using our secure function instead of direct table access
        console.log("Step 1: Using get_patients_by_clinic function");
        console.log("Search query for RPC:", searchQuery || '');
        
        // Call the RPC function instead of querying the table directly
        const { data: patientsData, error } = await supabase
          .rpc('get_patients_by_clinic', { search_query: searchQuery || '' });
        
        // Debug the RPC call
        console.log("RPC details:", {
          function: 'get_patients_by_clinic',
          searchQuery: searchQuery || '',
          hasError: !!error
        });
        
        if (error) {
          console.error("❌ Error fetching patients:", error.message);
          console.error("Error details:", error);
          throw error;
        }

        console.log(`✅ Found ${patientsData?.length || 0} patients:`, patientsData);

        // Handle case where no patients are found
        if (!patientsData || patientsData.length === 0) {
          console.log("⚠️ No patients found for clinic:", clinicId);
          return [];
        }

        // Step 2: Get latest triage for each patient
        const patientIds = patientsData.map(p => p.id);
        console.log("Step 2: Getting triage data for patient IDs:", patientIds);
        console.log("Patient IDs count:", patientIds.length);
        
        // Split into smaller chunks if there are many patients (to avoid URL too long errors)
        const maxChunkSize = 20;
        const patientChunks = [];
        
        for (let i = 0; i < patientIds.length; i += maxChunkSize) {
          patientChunks.push(patientIds.slice(i, i + maxChunkSize));
        }
        
        console.log("Split into chunks:", patientChunks.length);
        
        let allTriageData = [];
        
        for (const chunk of patientChunks) {
          console.log("Processing chunk with patient IDs:", chunk);
          const { data: chunkTriageData, error: triageError } = await supabase
            .from('triage_submissions')
            .select('patient_id, urgency, created_at, symptoms')
            .in('patient_id', chunk)
            .order('created_at', { ascending: false });
            
          if (triageError) {
            console.error(`❌ Error fetching triage data for chunk:`, triageError);
          } else {
            console.log(`✅ Found ${chunkTriageData?.length || 0} triage submissions for chunk`);
            if (chunkTriageData) {
              allTriageData = [...allTriageData, ...chunkTriageData];
            }
          }
        }
        
        console.log(`✅ Total triage submissions found: ${allTriageData.length}`);
        
        // Log which patients have triage data
        const patientsWithTriage = new Set(allTriageData.map(t => t.patient_id));
        console.log("Patients with triage data:", patientsWithTriage);
        console.log("Patients without triage data:", patientIds.filter(id => !patientsWithTriage.has(id)));

        // Step 3: Get upcoming appointments
        console.log("Step 3: Getting appointment data for patient IDs");
        
        let allAppointmentData = [];
        
        for (const chunk of patientChunks) {
          console.log("Processing appointments chunk with patient IDs:", chunk);
          const { data: chunkAppointmentData, error: appointmentError } = await supabase
            .from('appointments')
            .select('patient_id, start_time')
            .in('patient_id', chunk)
            .gte('start_time', new Date().toISOString())
            .order('start_time', { ascending: true });

          if (appointmentError) {
            console.error(`❌ Error fetching appointment data for chunk:`, appointmentError);
          } else {
            console.log(`✅ Found ${chunkAppointmentData?.length || 0} upcoming appointments for chunk`);
            if (chunkAppointmentData) {
              allAppointmentData = [...allAppointmentData, ...chunkAppointmentData];
            }
          }
        }
        
        console.log(`✅ Total upcoming appointments found: ${allAppointmentData.length}`);

        // Map the data together
        console.log("Step 4: Mapping all data together");
        const mappedPatients = patientsData.map(patient => {
          // Find all triage submissions for this patient
          const patientTriages = allTriageData?.filter(t => t.patient_id === patient.id) || [];
          // Sort by date descending if multiple exist (should already be sorted, but just to be safe)
          patientTriages.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
          // Take the most recent one
          const lastTriage = patientTriages[0];
          
          // Find upcoming appointments for this patient
          const patientAppointments = allAppointmentData?.filter(a => a.patient_id === patient.id) || [];
          // Sort by date ascending if multiple exist (should already be sorted, but just to be safe)
          patientAppointments.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
          // Take the soonest one
          const nextAppointment = patientAppointments[0];
          
          // Log the specific data for each patient
          console.log(`Patient ${patient.id} (${patient.first_name} ${patient.last_name}):`); 
          console.log({
            hasTriageData: !!lastTriage,
            hasAppointmentData: !!nextAppointment
          });

          return {
            ...patient,
            lastTriage: lastTriage ? {
              urgency: lastTriage.urgency,
              created_at: lastTriage.created_at,
              symptoms: lastTriage.symptoms
            } : undefined,
            nextAppointment: nextAppointment ? {
              start_time: nextAppointment.start_time
            } : undefined
          };
        });
        
        // Final validation check before returning
        if (!mappedPatients || mappedPatients.length === 0) {
          console.warn("⚠️ After mapping, patient data is empty. This should not happen if patientsData had entries.");
        } else {
          console.log("✅ Final mapped patient count:", mappedPatients.length);
        }
        
        return mappedPatients;
        
      } catch (error) {
        console.error("❌ CRITICAL ERROR in usePatientData:", error);
        throw error;
      }
    },
    // Add staleTime to prevent unnecessary refetches
    staleTime: 15000, // 15 seconds
    // Add refetch on window focus
    refetchOnWindowFocus: true,
    // Add retry on failure
    retry: 3,
  });
  
  // Additional debugging hook to log result changes
  useEffect(() => {
    console.log("usePatientData hook - current state:", {
      isLoading: queryResult.isLoading,
      isError: queryResult.isError,
      error: queryResult.error,
      data: queryResult.data ? `${queryResult.data.length} patients` : "No data",
      dataFirstItem: queryResult.data && queryResult.data.length > 0 ? queryResult.data[0] : null
    });
  }, [queryResult.data, queryResult.isLoading, queryResult.isError, queryResult.error]);
  
  return queryResult;
}
